/**
 * Modèles Anthropic par étape du pipeline — constantes UI.
 *
 * ⚠️ Miroir de `supabase/functions/_shared/pipeline-models.ts` (apps/app ne
 * peut pas importer les helpers Deno). Garder les deux fichiers synchronisés :
 * catalogue, étapes, défauts.
 */

/** Catalogue des modèles Anthropic sélectionnables, du moins au plus cher. */
export const PIPELINE_MODELS = {
  'claude-haiku-4-5-20251001': { label: 'Haiku 4.5' },
  'claude-sonnet-4-6': { label: 'Sonnet 4.6' },
  'claude-opus-4-7': { label: 'Opus 4.7' },
} as const;

export type PipelineModelId = keyof typeof PIPELINE_MODELS;

/** Identifiants de modèles, ordonnés du moins au plus cher. */
export const PIPELINE_MODEL_IDS = Object.keys(PIPELINE_MODELS) as PipelineModelId[];

/** Étapes IA qui consomment un appel LLM (pipeline + génération V2). */
export type PipelineLlmStep =
  | 'scenarios_generate'
  | 'scenarios_audit'
  | 'scenarios_regenerate'
  | 'matching'
  | 'statement_generate'
  | 'statement_verify'
  | 'solution_generate'
  | 'solution_verify'
  | 'distractors_generate'
  | 'v2_coherence'
  | 'v2_solution'
  | 'v2_audit'
  | 'v2_dictation'
  | 'v2_dictation_edit'
  | 'v2_figure'
  | 'epreuve_segment'
  | 'epreuve_extract_statements'
  | 'epreuve_extract_corrections'
  | 'blog_article_generate'
  | 'blog_article_evaluate'
  | 'dag_build'
  | 'v2_clue_hunt'
  | 'blueprint_extract'
  | 'visio_interview_summarize'
  | 'discussion_title_generate'
  | 'card_generate'
  | 'v2_scratch_model';

/** Rôle d'une étape : génération (créatif) ou vérification (critique). */
export type StepRole = 'generation' | 'verification';

/** Les 9 étapes IA, dans l'ordre du pipeline, avec leur rôle et leur libellé. */
export const PIPELINE_STEPS: { step: PipelineLlmStep; role: StepRole; label: string }[] = [
  { step: 'scenarios_generate', role: 'generation', label: 'Génération des scénarios' },
  { step: 'scenarios_audit', role: 'verification', label: 'Audit des scénarios' },
  { step: 'scenarios_regenerate', role: 'generation', label: 'Régénération d’un scénario' },
  { step: 'matching', role: 'generation', label: 'Matching curriculum' },
  { step: 'statement_generate', role: 'generation', label: 'Génération de l’énoncé' },
  { step: 'statement_verify', role: 'verification', label: 'Vérification de l’énoncé' },
  { step: 'solution_generate', role: 'generation', label: 'Génération du corrigé' },
  { step: 'solution_verify', role: 'verification', label: 'Vérification du corrigé' },
  { step: 'distractors_generate', role: 'generation', label: 'Génération des distracteurs' },
  // Génération V2 « blocs natifs ».
  { step: 'v2_coherence', role: 'generation', label: 'V2 — cohérence & segmentation' },
  { step: 'v2_solution', role: 'generation', label: 'V2 — corrigé pédagogique' },
  { step: 'v2_dictation', role: 'generation', label: 'V2 — structuration de dictée' },
  { step: 'v2_dictation_edit', role: 'generation', label: 'V2 — édition vocale d’un corrigé' },
  { step: 'v2_figure', role: 'generation', label: 'V2 — figure dictée' },
  { step: 'v2_audit', role: 'verification', label: 'V2 — audit du corrigé' },
  // Pipeline d'extraction des épreuves PDF → V2.
  { step: 'epreuve_segment', role: 'generation', label: 'Épreuve — segmentation du sujet' },
  {
    step: 'epreuve_extract_statements',
    role: 'generation',
    label: 'Épreuve — extraction de l’énoncé',
  },
  {
    step: 'epreuve_extract_corrections',
    role: 'generation',
    label: 'Épreuve — extraction du corrigé',
  },
  // Blog réflexif.
  {
    step: 'blog_article_generate',
    role: 'generation',
    label: 'Blog — génération d’un article réflexif',
  },
  // Évaluation « juge » → défaut Sonnet via le rôle (pas d'override).
  {
    step: 'blog_article_evaluate',
    role: 'verification',
    label: 'Blog — évaluation d’un article (juge)',
  },
  // DAG solve-backward → défaut Sonnet via le rôle (pas d'override).
  {
    step: 'dag_build',
    role: 'verification',
    label: 'DAG — construction solve-backward',
  },
  // Chasse aux indices V2 → défaut Sonnet via le rôle (pas d'override).
  {
    step: 'v2_clue_hunt',
    role: 'verification',
    label: 'V2 — chasse aux indices',
  },
  // Générateur paramétrique (Blueprint) → défaut Sonnet via le rôle (pas d'override).
  {
    step: 'blueprint_extract',
    role: 'verification',
    label: 'Blueprint — structuration paramétrique',
  },
  // Résumé d'un rendez-vous (dialogue du chat) → défaut Sonnet via le rôle (pas d'override).
  {
    step: 'visio_interview_summarize',
    role: 'verification',
    label: 'Visio — résumé d’un rendez-vous (dialogue du chat)',
  },
  // Titre d'une discussion « libre » déduit de son contenu — tâche simple, Haiku par défaut.
  {
    step: 'discussion_title_generate',
    role: 'generation',
    label: 'Discussion — titre déduit du contenu',
  },
  // SKILLCARD (63 × 88) : savoir du tuteur → recto structuré. Défaut Sonnet via le
  // rôle (qualité pédagogique ; PAS d'override → invariant Opus solo préservé).
  {
    step: 'card_generate',
    role: 'verification',
    label: 'SKILLCARD — génération du recto (63 × 88)',
  },
  // Feuille de brouillon V2 : modèle du moteur @jeraisonne/reasoner par question
  // (migration 0367). Modélisation fine (valeurs exactes, pièges faux) → Sonnet via
  // le rôle (PAS d'override → invariant Opus solo préservé).
  {
    step: 'v2_scratch_model',
    role: 'verification',
    label: 'V2 — feuille de brouillon (modèle du moteur)',
  },
];

/**
 * Modèle par défaut selon le rôle — abaissé d'un cran vs l'historique
 * (génération : Sonnet → Haiku ; vérification : Opus → Sonnet).
 */
export const DEFAULT_MODEL_BY_ROLE: Record<StepRole, PipelineModelId> = {
  generation: 'claude-haiku-4-5-20251001',
  verification: 'claude-sonnet-4-6',
};

/**
 * Surcharges de modèle par étape (vision PDF → Sonnet). Miroir de
 * `STEP_MODEL_OVERRIDES` côté Deno.
 */
export const STEP_MODEL_OVERRIDES: Partial<Record<PipelineLlmStep, PipelineModelId>> = {
  epreuve_segment: 'claude-sonnet-4-6',
  epreuve_extract_statements: 'claude-sonnet-4-6',
  epreuve_extract_corrections: 'claude-sonnet-4-6',
  // Reconstruire le LaTeX / les théorèmes depuis un oral bruité → Sonnet.
  v2_dictation: 'claude-sonnet-4-6',
  // Édition LaTeX ciblée + raisonnement géométrique (constrained_geometry) → Sonnet.
  v2_dictation_edit: 'claude-sonnet-4-6',
  v2_figure: 'claude-sonnet-4-6',
  // Article réflexif : profondeur éditoriale + justesse des penseurs → Opus.
  blog_article_generate: 'claude-opus-4-7',
};

/** Modèle par défaut de chaque étape (override explicite sinon dérivé du rôle). */
export const DEFAULT_STEP_MODELS: Record<PipelineLlmStep, PipelineModelId> = Object.fromEntries(
  PIPELINE_STEPS.map((s) => [
    s.step,
    STEP_MODEL_OVERRIDES[s.step] ?? DEFAULT_MODEL_BY_ROLE[s.role],
  ]),
) as Record<PipelineLlmStep, PipelineModelId>;
